import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { DescargaReporte } from "../components/DescargaReporte";
import { Link } from "../router";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";

// Apartado "Reportes" de Sigepro PR (núcleo privado) — ver README.md,
// Bloque 8. Organiza en un solo lugar, por módulo, todos los reportes
// exportables del sistema: algunos abren aquí mismo (formato + botón de
// descarga contra el motor genérico app/exportes_utils.py), y otros —los
// que ya tenían una pantalla propia con filtros y datos en pantalla, como
// los libros oficiales de Contabilidad o los desprendibles de Nómina—
// enlazan a esa pantalla en vez de duplicarla aquí.
//
// La organización sigue las fases/módulos reales de Sigepro PR (no la
// taxonomía de categorías de Siigo Nube, que corresponde a un producto de
// contabilidad de terceros con un modelo de datos distinto) — ver la nota
// de alcance al final de este archivo y en el README.

type Reporte = {
  titulo: string;
  descripcion?: string;
  endpoint?: string;
  params?: Record<string, string | number | undefined>;
  nombreBase?: string;
  formatos?: string[];
  enlace?: { to: string; texto: string };
};

type Categoria = {
  clave: string;
  titulo: string;
  permiso?: string;
  reportes: Reporte[];
};

function useLista<T = any>(permiso: boolean, url: string): T[] {
  const [datos, setDatos] = useState<T[]>([]);
  useEffect(() => {
    if (!permiso) return;
    api.get(url).then(setDatos).catch(() => setDatos([]));
  }, [permiso, url]);
  return datos;
}

export function Reportes() {
  const { tienePermiso } = useAuth();
  const hoy = new Date().toISOString().slice(0, 10);

  const lotesCosteados = useLista<{ id: number; codigoLote: string; costeado: boolean }>(
    tienePermiso("costeo:ver"), "/produccion/lotes",
  ).filter((l) => l.costeado);
  const [loteSeleccionado, setLoteSeleccionado] = useState<number | "">("");

  const turnosCerrados = useLista<{ id: number; caja: string; fechaCierre: string | null; estado: string }>(
    tienePermiso("pos:ver"), "/pos/turnos",
  ).filter((t) => t.estado === "cerrado");
  const [turnoSeleccionado, setTurnoSeleccionado] = useState<number | "">("");

  const comandas = useLista<{ id: number; numero: string; mesaNumero: string; estado: string }>(
    tienePermiso("restaurante:ver"), "/restaurante/comandas",
  );
  const [comandaSeleccionada, setComandaSeleccionada] = useState<number | "">("");

  const cuentasContables = useLista<{ id: number; codigo: string; nombre: string; aceptaMovimiento: boolean }>(
    tienePermiso("contabilidad:ver"), "/contabilidad/cuentas",
  ).filter((c) => c.aceptaMovimiento);
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState<number | "">("");

  const CATEGORIAS: Categoria[] = [
    {
      clave: "compras", titulo: "Compras y Recepción", permiso: "compras:ver",
      reportes: [
        { titulo: "Órdenes de compra", endpoint: "/compras/ordenes/exportar", nombreBase: "ordenes_compra" },
        { titulo: "Recepciones de materia prima", endpoint: "/compras/recepciones/exportar", nombreBase: "recepciones" },
        { titulo: "Facturas de compra (mercancía para reventa)", endpoint: "/compras/facturas-compra/exportar", nombreBase: "facturas_compra" },
        { titulo: "Documentos soporte", endpoint: "/compras/documentos-soporte/exportar", nombreBase: "documentos_soporte" },
      ],
    },
    {
      clave: "produccion", titulo: "Producción", permiso: "produccion:ver",
      reportes: [
        { titulo: "Lotes de producción", endpoint: "/produccion/lotes/exportar", nombreBase: "lotes_produccion" },
      ],
    },
    {
      clave: "costeo", titulo: "Costeo", permiso: "costeo:ver",
      reportes: [],
    },
    {
      clave: "inventarios", titulo: "Inventarios", permiso: "inventarios:ver",
      reportes: [
        { titulo: "Saldos de materia prima", endpoint: "/inventarios/materia-prima/exportar", nombreBase: "saldos_materia_prima" },
        { titulo: "Saldos de producto terminado", endpoint: "/inventarios/producto-terminado/exportar", nombreBase: "saldos_producto_terminado" },
        { titulo: "Kárdex de movimientos (últimos 500)", endpoint: "/inventarios/kardex/exportar", nombreBase: "kardex_movimientos" },
      ],
    },
    {
      clave: "comercial", titulo: "Comercial / Ventas", permiso: "comercial:ver",
      reportes: [
        { titulo: "Cotizaciones", endpoint: "/comercial/cotizaciones/exportar", nombreBase: "cotizaciones" },
        { titulo: "Pedidos", endpoint: "/comercial/pedidos/exportar", nombreBase: "pedidos" },
        { titulo: "Facturas de venta", endpoint: "/comercial/facturas/exportar", nombreBase: "facturas_venta" },
        { titulo: "Cartera por cobrar — antigüedad de saldos", endpoint: "/comercial/cartera-aging/exportar", params: { corte: hoy }, nombreBase: `cartera_cxc_${hoy}` },
        { titulo: "Seguimiento comercial (CRM y cobranza)", endpoint: "/comercial/seguimientos/exportar", nombreBase: "seguimientos_comerciales" },
      ],
    },
    {
      clave: "contabilidad", titulo: "Contabilidad y Estados Financieros", permiso: "contabilidad:ver",
      reportes: [
        { titulo: "Libro diario, libro mayor, balance de prueba, estado de resultados, balance general, flujo de efectivo, cambios en el patrimonio y libros oficiales de IVA (ventas/compras/inventarios y balances)",
          enlace: { to: "/contabilidad", texto: "Ir a Contabilidad →" } },
        { titulo: "Retenciones aplicadas", endpoint: "/contabilidad/retenciones/exportar", nombreBase: "retenciones" },
      ],
    },
    {
      clave: "nomina", titulo: "Nómina y Cumplimiento Laboral", permiso: "nomina:ver",
      reportes: [
        { titulo: "Listado de empleados", endpoint: "/nomina/empleados/exportar", nombreBase: "empleados" },
        { titulo: "Liquidaciones de nómina", endpoint: "/nomina/liquidaciones/exportar", nombreBase: "liquidaciones_nomina" },
        { titulo: "Aportes a seguridad social", endpoint: "/nomina/seguridad-social/exportar", nombreBase: "seguridad_social" },
        { titulo: "Prestaciones sociales", endpoint: "/nomina/prestaciones-sociales/exportar", nombreBase: "prestaciones_sociales" },
        { titulo: "Desprendibles de pago y certificados laborales (por empleado)", enlace: { to: "/nomina", texto: "Ir a Nómina →" } },
      ],
    },
    {
      clave: "cumplimiento", titulo: "Cumplimiento DIAN", permiso: "cumplimiento:ver",
      reportes: [
        { titulo: "Información exógena y declaraciones tributarias", enlace: { to: "/cumplimiento", texto: "Ir a Cumplimiento DIAN →" } },
      ],
    },
    {
      clave: "pos", titulo: "POS (mostrador)", permiso: "pos:ver",
      reportes: [
        { titulo: "Ventas de mostrador", endpoint: "/pos/ventas/exportar", nombreBase: "ventas_pos" },
      ],
    },
    {
      clave: "restaurante", titulo: "Restaurante (mesas y comandas)", permiso: "restaurante:ver",
      reportes: [
        { titulo: "Listado de comandas", endpoint: "/restaurante/comandas/exportar", nombreBase: "comandas" },
      ],
    },
    {
      clave: "notas", titulo: "Notas crédito/débito", permiso: "notas:ver",
      reportes: [
        { titulo: "Notas crédito y débito (documento XML por nota)", enlace: { to: "/notas", texto: "Ir a Notas crédito/débito →" } },
      ],
    },
    {
      clave: "tesoreria", titulo: "Tesorería", permiso: "tesoreria:ver",
      reportes: [
        { titulo: "Cuentas bancarias", endpoint: "/tesoreria/cuentas/exportar", nombreBase: "cuentas_bancarias" },
        { titulo: "Movimientos de tesorería", endpoint: "/tesoreria/movimientos/exportar", nombreBase: "movimientos_tesoreria" },
        { titulo: "Cuentas por pagar — antigüedad de saldos", endpoint: "/tesoreria/cartera-cxp-aging/exportar", params: { corte: hoy }, nombreBase: `cartera_cxp_${hoy}` },
        { titulo: "Flujo de caja proyectado", endpoint: "/tesoreria/flujo-caja/exportar", nombreBase: `flujo_caja_${hoy}` },
      ],
    },
    {
      clave: "activos", titulo: "Activos Fijos", permiso: "activos:ver",
      reportes: [
        { titulo: "Listado de activos fijos (con depreciación acumulada y valor en libros)", endpoint: "/activos/exportar", nombreBase: "activos_fijos" },
        { titulo: "Depreciaciones registradas", endpoint: "/activos/depreciaciones/exportar", nombreBase: "depreciaciones_activos" },
        { titulo: "Traslados y bajas", endpoint: "/activos/movimientos/exportar", nombreBase: "movimientos_activos" },
      ],
    },
    {
      clave: "auditoria", titulo: "Auditoría", permiso: "core.auditoria:ver",
      reportes: [
        { titulo: "Bitácora de auditoría (últimos 500 eventos)", endpoint: "/auditoria/exportar", nombreBase: "bitacora_auditoria" },
      ],
    },
  ];

  const categoriasVisibles = CATEGORIAS.filter((c) => !c.permiso || tienePermiso(c.permiso));

  return (
    <Shell titulo="Reportes" breadcrumb="Core / Reportes">
      <p className="text-sm text-textmuted mb-5 max-w-3xl">
        Todos los reportes exportables de Sigepro PR, organizados por módulo. Los reportes con selector de formato se
        descargan directamente desde aquí (PDF, Excel o Word); los que ya tienen su propia pantalla con filtros
        (libros contables, desprendibles de nómina, notas, información exógena) enlazan a esa pantalla.
      </p>

      <div className="space-y-6">
        {categoriasVisibles.map((cat) => (
          <div key={cat.clave} className="card p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">{cat.titulo}</h3>

            {cat.clave === "costeo" && (
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <select
                  className="rounded-lg border border-borderc bg-white px-2 py-2 text-xs text-slate-700 focus:outline-none focus:ring-4 focus:ring-accent/15 focus:border-accent"
                  value={loteSeleccionado} onChange={(e) => setLoteSeleccionado(e.target.value ? Number(e.target.value) : "")}
                >
                  <option value="">Seleccione un lote costeado…</option>
                  {lotesCosteados.map((l) => <option key={l.id} value={l.id}>{l.codigoLote}</option>)}
                </select>
                {loteSeleccionado !== "" && (
                  <DescargaReporte endpoint={`/costeo/lotes/${loteSeleccionado}/exportar`} nombreBase={`costeo_lote_${loteSeleccionado}`} label="Descargar ficha de costeo" />
                )}
                {lotesCosteados.length === 0 && <span className="text-xs text-textmuted italic">Aún no hay lotes con costeo calculado.</span>}
              </div>
            )}

            <div className="divide-y divide-borderc">
              {cat.reportes.map((r, i) => (
                <div key={i} className="py-3 first:pt-0 last:pb-0 flex items-center justify-between gap-4 flex-wrap">
                  <div className="max-w-xl">
                    <p className="text-sm text-slate-700">{r.titulo}</p>
                    {r.descripcion && <p className="text-xs text-textmuted mt-0.5">{r.descripcion}</p>}
                  </div>
                  {r.enlace ? (
                    <Link to={r.enlace.to} className="text-sm font-semibold text-accent hover:underline whitespace-nowrap">{r.enlace.texto}</Link>
                  ) : (
                    <DescargaReporte endpoint={r.endpoint!} params={r.params} nombreBase={r.nombreBase!} formatos={r.formatos} />
                  )}
                </div>
              ))}
            </div>

            {cat.clave === "pos" && (
              <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-borderc">
                <span className="text-sm text-slate-700 mr-1">Cierre de un turno de caja:</span>
                <select
                  className="rounded-lg border border-borderc bg-white px-2 py-2 text-xs text-slate-700 focus:outline-none focus:ring-4 focus:ring-accent/15 focus:border-accent"
                  value={turnoSeleccionado} onChange={(e) => setTurnoSeleccionado(e.target.value ? Number(e.target.value) : "")}
                >
                  <option value="">Seleccione un turno cerrado…</option>
                  {turnosCerrados.map((t) => <option key={t.id} value={t.id}>{t.caja} — {t.fechaCierre}</option>)}
                </select>
                {turnoSeleccionado !== "" && (
                  <DescargaReporte endpoint={`/pos/turnos/${turnoSeleccionado}/exportar`} nombreBase={`cierre_turno_${turnoSeleccionado}`} label="Descargar cierre de turno" />
                )}
                {turnosCerrados.length === 0 && <span className="text-xs text-textmuted italic">Aún no hay turnos cerrados.</span>}
              </div>
            )}

            {cat.clave === "restaurante" && (
              <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-borderc">
                <span className="text-sm text-slate-700 mr-1">Ficha de una comanda puntual:</span>
                <select
                  className="rounded-lg border border-borderc bg-white px-2 py-2 text-xs text-slate-700 focus:outline-none focus:ring-4 focus:ring-accent/15 focus:border-accent"
                  value={comandaSeleccionada} onChange={(e) => setComandaSeleccionada(e.target.value ? Number(e.target.value) : "")}
                >
                  <option value="">Seleccione una comanda…</option>
                  {comandas.map((c) => <option key={c.id} value={c.id}>{c.numero} — Mesa {c.mesaNumero} ({c.estado})</option>)}
                </select>
                {comandaSeleccionada !== "" && (
                  <DescargaReporte endpoint={`/restaurante/comandas/${comandaSeleccionada}/exportar`} nombreBase={`comanda_${comandaSeleccionada}`} label="Descargar comanda" />
                )}
                {comandas.length === 0 && <span className="text-xs text-textmuted italic">Aún no hay comandas registradas.</span>}
              </div>
            )}

            {cat.clave === "contabilidad" && (
              <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-borderc">
                <span className="text-sm text-slate-700 mr-1">Libro mayor de una cuenta puntual:</span>
                <select
                  className="rounded-lg border border-borderc bg-white px-2 py-2 text-xs text-slate-700 focus:outline-none focus:ring-4 focus:ring-accent/15 focus:border-accent"
                  value={cuentaSeleccionada} onChange={(e) => setCuentaSeleccionada(e.target.value ? Number(e.target.value) : "")}
                >
                  <option value="">Seleccione una cuenta…</option>
                  {cuentasContables.map((c) => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
                </select>
                {cuentaSeleccionada !== "" && (
                  <DescargaReporte endpoint="/contabilidad/libro-mayor/exportar" params={{ cuentaId: cuentaSeleccionada }} nombreBase={`libro_mayor_${cuentaSeleccionada}`} label="Descargar libro mayor" />
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </Shell>
  );
}
